import { Injectable } from '@angular/core';
import { PigGenderType } from '../../model/pig-gender-type';
import { StoreAnimalWeights } from '../../../storage/data-store/model/store-animal-weights';
import { PigGenderTypes } from '../../model/pig-gender-types';

@Injectable({
  providedIn: 'root',
})
export class BatchWeighingDomainService {
  private MaximumVarianceInInitialWeighingKg: number = 0.25;
  private roundingPlaces: number = 3;
  private static readonly roundingPlacesStatic: number = 3;

  numberOfAnimals(numAnimals: number[]): number {
    return numAnimals.filter((t) => t > 0).reduce((sum, value) => Number(sum) + Number(value), 0);
  }

  totalWeight(weighings: number[]): number {
    return weighings.filter((t) => t > 0).reduce((sum, value) => Number(sum) + Number(value), 0);
  }

  averageWeightRounded(animals: number[], weighings: number[]): number {
    const result = this.roundNumber(this.averageWeight(animals, weighings), this.roundingPlaces);
    return result;
  }

  averageWeightPerPenRounded(numberOfAnimals: number, weight: number): number {
    return this.roundNumber(this.averageWeightForPen(numberOfAnimals, weight), this.roundingPlaces);
  }

  averageWeightForPen(numberOfAnimals: number, weight: number): number {
    if (numberOfAnimals <= 0 || weight <= 0) {
      return 0;
    }
    return Number((weight / numberOfAnimals).toFixed(3));
  }

  private roundNumber(value: number, places: number): number {
    return Number.parseFloat(value.toFixed(places).toString());
  }

  private averageWeight(animals: number[], weighings: number[]): number {
    const numberOfAnimals: number = this.numberOfAnimals(animals);
    const totalWeight: number = this.totalWeight(weighings);
    if (numberOfAnimals <= 0 || totalWeight <= 0) {
      return 0;
    }
    return this.totalWeight(weighings) / numberOfAnimals;
  }

  static averageWeightPerPenRounded(numberOfAnimals: number, weight: number): number {
    return this.roundNumber(this.averageWeightForPen(numberOfAnimals, weight), this.roundingPlacesStatic);
  }

  private static roundNumber(value: number, places: number): number {
    return Number.parseFloat(value.toFixed(places).toString());
  }

  private static averageWeightForPen(numberOfAnimals: number, weight: number): number {
    if (numberOfAnimals <= 0 || weight <= 0) {
      return 0;
    }
    return Number((weight / numberOfAnimals).toFixed(3));
  }

  static averageWeightRounded(animals: number[], weighings: number[]): number {
    const result = this.roundNumber(this.averageWeight(animals, weighings), this.roundingPlacesStatic);
    return result;
  }

  private static averageWeight(animals: number[], weighings: number[]): number {
    const numberOfAnimals: number = this.numberOfAnimals(animals);
    const totalWeight: number = this.totalWeight(weighings);
    if (numberOfAnimals <= 0 || totalWeight <= 0) {
      return 0;
    }
    return this.totalWeight(weighings) / numberOfAnimals;
  }

  static numberOfAnimals(numAnimals: number[]): number {
    return numAnimals.filter((t) => t > 0).reduce((sum, value) => Number(sum) + Number(value), 0);
  }

  static totalWeight(weighings: number[]): number {
    return weighings.filter((t) => t > 0).reduce((sum, value) => Number(sum) + Number(value), 0);
  }

  // Checks if this pens average weight is an outlier in terms of average weight compared to the other pens.
  // The difference between the largest and the smallest pen must not be larger than the allowed variance.
  // If the there a greater than allowed variance, the the pen with the largest or smallest weight is considered an outlier.
  // Pens that have a weight that is signficanlty larger or smaller than the median weight are also considered outliers.
  isOutlierWeightForPen(numberOfAnimals: number, weight: number, weighings: StoreAnimalWeights[]): boolean {
    if (numberOfAnimals <= 0 || weight <= 0) {
      return false;
    }
    const penAverageWeight: number = this.averageWeightForPen(numberOfAnimals, weight);
    const sortedAverageWeighings = weighings
      .filter((t) => t.animals > 0)
      .map((t) => this.averageWeightForPen(t.animals, t.weight))
      .sort((a, b) => a - b);

    if (sortedAverageWeighings.length < 1) {
      return false;
    }

    const minWeight = sortedAverageWeighings[0] ?? 0;
    const maxWeight = sortedAverageWeighings[sortedAverageWeighings.length - 1] ?? 0;
    const midWeightIndex = Math.floor(sortedAverageWeighings.length / 2);
    const medianWeight: number = sortedAverageWeighings[midWeightIndex] ?? 0;

    const isVarianceLargerThanAllowed: boolean = this.isVarianceLargerThanAllowed(minWeight, maxWeight);

    // No outliers exist
    if (!isVarianceLargerThanAllowed) {
      return false;
    }

    // If this pen has the smallest weight and thus an outlier
    if (penAverageWeight === minWeight && penAverageWeight !== medianWeight) {
      return true;
    }
    // If this pen has the largest weight and thus an outlier
    else if (penAverageWeight === maxWeight && penAverageWeight !== medianWeight) {
      return true;
    }
    // If the average weight is larger than allowed variance from the median weight then it is definitely an outlier
    // For example: If this pen has a signifcantly larger or smaller weight than the median, but it is not the largest or smallest.
    else if (
      penAverageWeight < medianWeight - this.MaximumVarianceInInitialWeighingKg ||
      penAverageWeight > medianWeight + this.MaximumVarianceInInitialWeighingKg
    ) {
      return true;
    }

    return false;
  }

  isVarianceLargerThanAllowed(minWeight: number, maxWeight: number): boolean {
    return maxWeight - minWeight > this.MaximumVarianceInInitialWeighingKg;
  }

  GetPigGenders(): PigGenderType[] {
    const genders = [
      PigGenderTypes.Sow,
      PigGenderTypes.Castrate,
      PigGenderTypes.MixedControlled,
      PigGenderTypes.MixedUnknown,
      PigGenderTypes.MalePig,
    ];
    const members = Object.keys(PigGenderTypes);
    if (members.length !== genders.length) {
      throw new Error('Missing genders in PigGenders');
    }
    return genders;
  }
}
